import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n";
import { waLink } from "@/whatsapp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758448511322-8bfc73daf606?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhcmNoaXRlY3R1cmUlMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3OTI0OTIxMTd8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-[115%] object-cover brightness-[0.85]" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-16">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] text-gold mb-8"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ivory">
            <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.5}>{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.65}>
              <em className="text-gold not-italic font-medium italic">{t.hero.line3}</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-7 text-base sm:text-lg leading-relaxed text-slate-300 max-w-xl"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={waLink(t.wa.msgVisit)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-cta"
              className="cta-glow group inline-flex items-center justify-center gap-3 bg-gold text-obsidian font-bold text-sm tracking-[0.08em] px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/#forfaits"
              data-testid="hero-packages-cta"
              className="inline-flex items-center justify-center gap-3 border border-ivory/25 text-ivory font-medium text-sm tracking-wide px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-5 text-xs tracking-wide text-slate-500"
            data-testid="hero-whatsapp-note"
          >
            {t.wa.note} : <span className="text-gold font-medium">{t.wa.number}</span>
          </motion.p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8"
      >
        <div className="grid grid-cols-3 border-t border-ivory/15 py-7 gap-4">
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-serif text-2xl sm:text-4xl font-semibold text-gold">{s.value}</p>
              <p className="mt-1 text-[11px] sm:text-xs tracking-wide text-slate-400 uppercase">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="absolute -top-16 right-8 hidden md:block">
          <ChevronDown className="w-5 h-5 text-gold animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
};
